import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { TemperatureBadge } from "@/components/crm/Badges";
import { fullName, relDate, isInvalidPhone } from "@/lib/crm";
import { Button } from "@/components/ui/button";
import { Phone, Check, Loader2, AlertTriangle, CalendarDays, Clock3, CheckCheck, Pencil, Trash2, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { EditActivityModal } from "@/components/crm/EditActivityModal";
import { EditAppointmentModal } from "@/components/crm/EditAppointmentModal";
import ActivityAppointments from "@/components/crm/ActivityAppointments";
import { useAuth } from "@/context/AuthContext";
import { usePageState } from "@/hooks/usePageState";

const GROUPS = [
  { key: "overdue", label: "Scadute", icon: AlertTriangle, color: "text-amber-500" },
  { key: "today", label: "Oggi", icon: CalendarDays, color: "text-blue-500" },
  { key: "upcoming", label: "Prossime", icon: Clock3, color: "text-sky-500" },
  { key: "completed", label: "Completate", icon: CheckCheck, color: "text-emerald-500" },
  { key: "appointments", label: "Appuntamenti", icon: CalendarClock, color: "text-indigo-500" },
];

export default function Activities() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = usePageState("activities_tab", "today");
  const [editTask, setEditTask] = useState(null);
  const [editAppt, setEditAppt] = useState(null);
  const { isAdmin } = useAuth();

  const load = () => Promise.all([api.get("/tasks"), api.get("/appointments")]).then(([t, a]) => setData({
    ...t.data, appointments: (a.data || []).filter((x) => x.status === "DA FARE"),
  }));
  useEffect(() => { load(); }, []);

  const complete = async (id) => {
    await api.post(`/tasks/${id}/complete`);
    toast.success("Attività completata");
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Sei sicuro di voler eliminare questa attività?")) return;
    await api.delete(`/tasks/${id}`);
    toast.success("Attività eliminata");
    load();
  };

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  const items = data[tab] || [];

  return (
    <div className="space-y-4 animate-fade-up">
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-black tracking-tight">Attività</h1>
        <p className="text-sm text-muted-foreground">Ogni attività è collegata a un lead.</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {GROUPS.map((g) => (
          <button key={g.key} data-testid={`activity-tab-${g.key}`} onClick={() => setTab(g.key)}
            className={cn("inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium border transition-colors",
              tab === g.key ? "bg-primary text-primary-foreground border-primary" : "bg-card hover:bg-accent")}>
            <g.icon className={cn("w-4 h-4", tab === g.key ? "" : g.color)} />
            {g.label}
            <span className={cn("ml-1 px-1.5 rounded-full text-xs", tab === g.key ? "bg-primary-foreground/20" : "bg-muted")}>{(data[g.key] || []).length}</span>
          </button>
        ))}
      </div>

      <div className="rounded-lg border bg-card overflow-hidden">
        {tab === "appointments" ? (
          <ActivityAppointments appts={items} isAdmin={isAdmin} onEdit={setEditAppt} onChanged={load} />
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground text-sm">Nessuna attività in questo gruppo.</div>
        ) : (
          <ul className="divide-y">
            {items.map((t) => (
              <li key={t.id} data-testid={`activity-${t.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-muted/40 transition-colors">
                {t.lead && <TemperatureBadge value={t.lead.temperature} />}
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{t.title} {t.lead && <span className="text-muted-foreground font-normal">· {fullName(t.lead)}</span>}</div>
                  <div className="text-xs text-muted-foreground">{t.type === "no_show_recontact" ? "No-show" : t.type === "call" ? "Chiamata" : "Follow-up"} · {relDate(t.due_at)}{t.lead?.status === "Mandare mail" ? " · Mandare mail" : ""}</div>
                </div>
                {t.lead?.phone && !isInvalidPhone(t.lead.phone) && <a href={`tel:${t.lead.phone}`} className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-emerald-500 text-white hover:bg-emerald-600"><Phone className="w-4 h-4" /></a>}
                {t.lead && <Button size="sm" variant="secondary" onClick={() => navigate(`/leads/${t.lead.id}`)}>Apri</Button>}
                {tab !== "completed" && <Button size="sm" variant="outline" data-testid={`edit-${t.id}`} onClick={() => setEditTask(t)} className="gap-1" title="Modifica attività"><Pencil className="w-3.5 h-3.5" /></Button>}
                {tab !== "completed" && <Button size="sm" data-testid={`complete-${t.id}`} onClick={() => complete(t.id)} className="gap-1"><Check className="w-3.5 h-3.5" /></Button>}
                <Button size="sm" variant="outline" data-testid={`delete-${t.id}`} onClick={() => remove(t.id)} className="gap-1 text-red-600 hover:bg-red-500/10 border-red-500/30" title="Elimina attività"><Trash2 className="w-3.5 h-3.5" /></Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <EditAppointmentModal appt={editAppt} open={!!editAppt} onClose={() => setEditAppt(null)} onDone={load} />
      <EditActivityModal task={editTask} open={!!editTask} onClose={() => setEditTask(null)} onDone={load} />
    </div>
  );
}
